import React, { useState, useEffect } from 'react'

// Fire emoji particles that float up from an element
export const FireParticles: React.FC<{ active?: boolean; intensity?: number }> = ({ active = true, intensity = 3 }) => {
  if (!active) return null
  return (
    <div className="absolute -top-2 left-0 right-0 pointer-events-none overflow-visible">
      {Array.from({ length: intensity }).map((_, i) => (
        <span
          key={i}
          className="absolute animate-float-up text-sm"
          style={{
            left: `${20 + Math.random() * 60}%`,
            animationDelay: `${i * 0.3}s`,
            animationDuration: `${1 + Math.random() * 0.5}s`,
          }}
        >
          🔥
        </span>
      ))}
    </div>
  )
}

// Confetti burst on trigger
export const ConfettiBurst: React.FC<{ trigger: boolean }> = ({ trigger }) => {
  const [particles, setParticles] = useState<Array<{ id: number; x: number; color: string }>>([])

  useEffect(() => {
    if (trigger) {
      const colors = ['#f97316', '#ef4444', '#eab308', '#22c55e', '#3b82f6', '#a855f7']
      setParticles(Array.from({ length: 12 }).map((_, i) => ({
        id: Date.now() + i,
        x: Math.random() * 100,
        color: colors[Math.floor(Math.random() * colors.length)],
      })))
      setTimeout(() => setParticles([]), 1500)
    }
  }, [trigger])

  if (particles.length === 0) return null
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute animate-confetti w-2 h-2 rounded-full"
          style={{
            left: `${p.x}%`,
            bottom: '30%',
            backgroundColor: p.color,
            animationDelay: `${Math.random() * 0.3}s`,
          }}
        />
      ))}
    </div>
  )
}

// Streak badge with flames
export const StreakBadge: React.FC<{ streak: number; className?: string }> = ({ streak, className }) => {
  if (streak < 2) return null
  const isHot = streak >= 5
  const isOnFire = streak >= 10

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
      isOnFire ? 'bg-streak-fire text-fire' :
      isHot ? 'bg-orange-500/15 text-orange-400' :
      'bg-amber-500/10 text-amber-400'
    } ${className || ''}`}>
      {isOnFire ? '🔥🔥' : isHot ? '🔥' : '⚡'} {streak} streak
    </span>
  )
}

// Pulsing live indicator
export const LivePulse: React.FC<{ label?: string; className?: string }> = ({ label = 'LIVE', className }) => (
  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-500/20 text-red-400 ${className || ''}`}>
    <span className="relative flex h-2 w-2">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
      <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
    </span>
    {label}
  </span>
)

// Score change animation
export const ScorePopup: React.FC<{ value: string; show: boolean }> = ({ value, show }) => {
  if (!show) return null
  return (
    <span className="absolute -top-4 left-1/2 -translate-x-1/2 animate-float-up text-sm font-bold text-orange-400 pointer-events-none whitespace-nowrap">
      +{value}
    </span>
  )
}

// Hot card wrapper — adds fire border and optional particles
export const HotCard: React.FC<{
  children: React.ReactNode
  isHot?: boolean
  className?: string
}> = ({ children, isHot, className }) => (
  <div className={`relative ${isHot ? 'border-hot' : ''} ${className || ''}`}>
    {isHot && <FireParticles intensity={2} />}
    {children}
  </div>
)

// Upset alert banner
export const UpsetAlert: React.FC<{ teamName: string; seed: number; opponentSeed: number }> = ({ teamName, seed, opponentSeed }) => {
  if (seed <= opponentSeed) return null // Not an upset
  return (
    <div className="animate-upset bg-gradient-to-r from-red-500/20 via-orange-500/20 to-red-500/20 border border-red-500/20 rounded-lg px-3 py-1.5 text-center">
      <span className="text-xs font-bold text-fire">🚨 UPSET ALERT</span>
      <span className="text-xs text-white/60 ml-2">#{seed} {teamName} takes down #{opponentSeed}!</span>
    </div>
  )
}
